import React, { useEffect, useRef, useState } from 'react';
import { ilClinicUiUrl, ukLoginHost } from '../config/appMode';
import { clearHandoffAttempts, redirectToIlLogin } from '../config/ilHandoff';

/**
 * The Israel-tenant host's "login page", which asks for nothing.
 *
 * Israel owns these identities, so instead of a form this sends them to the
 * Israeli system, carrying the page they were trying to reach. If their
 * Israeli session is still alive they come straight back, signed in, and never
 * see this.
 *
 * There is no form here and no choice to make: this host IS the Israeli door.
 * Anybody who signs in with a UK password uses the ordinary host instead
 * (dev-psyclinic / psy), which is the same app and the same tenant - so the
 * fallback still exists, it simply lives at the other address. The stuck state
 * below says so, because by then the person needs a way out rather than a
 * preference.
 *
 * `location` is read on mount rather than taken from a route parameter,
 * because this also stands in for the catch-all route: whatever URL somebody
 * opened is the thing they wanted to come back to.
 */
export default function IlLoginRedirectPage() {
  const [stuck, setStuck] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!redirectToIlLogin()) setStuck(true);
  }, []);

  // The cap counts attempts in this tab and nothing clears it on its own, so
  // without this the screen stays a dead end even after whatever was broken is
  // fixed - the only way out was to close the tab, which nobody guesses.
  const tryAgain = () => {
    clearHandoffAttempts();
    if (!redirectToIlLogin()) setStuck(true);
  };

  if (stuck) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow">
          <h1 className="mb-2 text-lg font-semibold text-gray-900">
            Sign in from the Israeli system
          </h1>
          <p className="mb-4 text-sm text-gray-600">
            This clinic is reached through the Israeli system, which did not
            manage to sign you in.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={tryAgain}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Try again
            </button>
            <a
              href={ilClinicUiUrl()}
              className="inline-block rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Open the Israeli system
            </a>
          </div>
          {/* Not an alternative being offered, a way out of a dead end: this
              only shows after the sign-in has already failed twice. */}
          <p className="mt-4 text-xs text-gray-400">
            If you have a UK password, sign in at{' '}
            <span className="font-medium">{ukLoginHost()}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <p className="text-sm text-gray-500">Taking you to sign in…</p>
    </div>
  );
}
